"use client";

import { useEffect, useState, useRef } from "react";
import Banner from "@/components/Banner";
import Events from "@/components/Events/EventCards";
import Category from "@/components/Events/Category";
import Breadcrumbs from "@/components/Breadcrumbs";
import EventSlider from "@/components/Events/MainEvents";
import Pagination from "@/components/Pagination";

import {
  GetAllEvents,
  getEventCategories,
  getEventMain,
} from "@/helpers/EventApis";
const buildHref = (page) => {
  return page > 1 ? `/events?page=${page}` : `/events`;
};
/* ---------------- Skeleton ---------------- */
const BlogCardSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div key={i} className="animate-pulse">
        <div className="bg-gray-200 h-48 rounded-t-lg"></div>
        <div className="p-4 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    ))}
  </div>
);

const Event = ({ initialPage = 1, initialData, initialCategories, initialMain }) => {
  const pageSize = 6;

  // Init categories
  const [categories, setCategories] = useState(() => {
    if (initialCategories?.data) {
      return [{ titel: "All Events", id: null }, ...initialCategories.data];
    }
    return [];
  });

  const [activeCategory, setActiveCategory] = useState("All Events");

  // Init events
  const [events, setEvents] = useState(initialData?.data || []);
  
  // Init awards
  const [awards, setAwards] = useState(() => {
    if (initialMain?.data?.multiple_image_for_banner) {
      return initialMain.data.multiple_image_for_banner.map((item, index) => ({
        img:
          item.image?.formats?.large?.url ||
          item.image?.formats?.medium?.url ||
          item.image?.url ||
          "/images/placeholder.webp",
        alt: item.image_titel || `Event ${index + 1}`,
        text: item.image_description || "Medhavi Skills University – Events",
      }));
    }
    return [];
  });

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pagination, setPagination] = useState({
    page: initialData?.meta?.pagination?.page || initialPage,
    pageSize: initialData?.meta?.pagination?.pageSize || pageSize,
    pageCount: initialData?.meta?.pagination?.pageCount || 1,
    total: initialData?.meta?.pagination?.total || 0,
  });
  
  const skipUrlSyncRef = useRef(false);
  const isInitialMount = useRef(true);
  const [loading, setLoading] = useState(false); // Start false since we have initial data

  /* ---------------- Events Fetch ---------------- */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip fetch on first mount since we have SSR data
    }

    const fetchEvents = async () => {
      setLoading(true);

      const res = await GetAllEvents(activeCategory, currentPage, pageSize);

      if (res?.data) {
        setEvents(res.data);
        setPagination({
          page: res.meta.pagination.page || 1,
          pageSize: res.meta.pagination.pageSize || pageSize,
          pageCount: res.meta.pagination.pageCount || 1,
          total: res.meta.pagination.total || 0,
        });
      } else {
        setEvents([]);
        setPagination({
          page: 1,
          pageSize,
          pageCount: 1,
          total: 0,
        });
      }

      setLoading(false);
    };

    fetchEvents();
  }, [activeCategory, currentPage, pageSize]);

  // Link for next and previous page
  useEffect(() => {
    // Remove previously added pagination links
    const existingPrev = document.querySelector('link[rel="prev"]');
    const existingNext = document.querySelector('link[rel="next"]');

    existingPrev?.remove();
    existingNext?.remove();

    // Add previous page link
    if (currentPage > 1) {
      const prevLink = document.createElement("link");
      prevLink.rel = "prev";
      prevLink.href = buildHref(currentPage - 1);
      document.head.appendChild(prevLink);
    }

    // Add next page link
    if (currentPage < pagination.pageCount) {
      const nextLink = document.createElement("link");
      nextLink.rel = "next";
      nextLink.href = buildHref(currentPage + 1);
      document.head.appendChild(nextLink);
    }

    // Cleanup when page changes or component unmounts
    return () => {
      document.querySelector('link[rel="prev"]')?.remove();
      document.querySelector('link[rel="next"]')?.remove();
    };
  }, [currentPage, pagination.pageCount]);
  /* ---------------- Render ---------------- */
  return (
    <div className="w-full overflow-x-hidden">
      <Banner
        BannerImage="https://cdn.msu.edu.in/campus/event-banner.webp"
        banner_name="Events"
        banner_tag_line="Moments, Milestones and more at MSU"
      />

      <Breadcrumbs items={[{ label: "Events", href: "/events" }]} />

      {awards.length > 0 && (
        <section className="mt-4 md:mt-6">
          <EventSlider awards={awards} />
        </section>
      )}

      <Category
        categories={categories}
        activeCategory={activeCategory}
        handleCategory={(cat) => {
          if (!cat?.titel) return;
          setActiveCategory(cat.titel);
          setCurrentPage(1);
        }}
      />

      <section className="w-full">
        <div className="max-w-[var(--max-page-width)] mx-auto px-[var(--container-padding)]">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold my-4">
            {activeCategory}
          </h2>

          {loading ? <BlogCardSkeleton /> : <Events events={events} />}
        </div>
      </section>

      {pagination.pageCount > 1 && (
        <section className="container mx-auto px-3 sm:px-6 lg:px-12 my-8">
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.pageCount}
            setPage={(page) => {
              skipUrlSyncRef.current = true;
              setCurrentPage(page);
              window.scrollTo({ top: window.innerHeight * 1.5, behavior: "smooth" });
            }}
            buildHref={buildHref}


          />
        </section>
      )}
    </div>
  );
};

export default Event;